import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { salaryGradeApi, timesheetApi, payrollApi } from '../../api/payroll';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';

export function PayrollPage() {
  const { showSuccess, showError } = useToast();
  const canDelete = usePermission('payroll.delete');
  const canDeleteGrades = usePermission('salary_grades.delete');
  const queryClient = useQueryClient();

  // ---- Salary Grades ----
  const gradesQuery = useQuery({ queryKey: ['salary-grades'], queryFn: () => salaryGradeApi.list() });
  const gradeForm = useForm<{ gradeName: string; defaultMonthlySalary: string }>();
  const createGradeMutation = useMutation({
    mutationFn: (v: { gradeName: string; defaultMonthlySalary: string }) => salaryGradeApi.create({ gradeName: v.gradeName, defaultMonthlySalary: Number(v.defaultMonthlySalary) }),
    onSuccess: () => { showSuccess('Salary grade added.'); queryClient.invalidateQueries({ queryKey: ['salary-grades'] }); gradeForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const deleteGradeMutation = useMutation({
    mutationFn: (id: number) => salaryGradeApi.delete(id),
    onSuccess: () => { showSuccess('Salary grade deleted.'); queryClient.invalidateQueries({ queryKey: ['salary-grades'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete.'),
  });

  // ---- Timesheets ----
  const pendingTimesheetsQuery = useQuery({ queryKey: ['timesheets-pending'], queryFn: () => timesheetApi.pending() });
  const bulkTimesheetForm = useForm<{ periodMonth: string; daysExpected: string }>();
  const bulkGenerateTimesheetsMutation = useMutation({
    mutationFn: (v: { periodMonth: string; daysExpected: string }) => timesheetApi.bulkGenerate({ periodMonth: v.periodMonth, daysExpected: Number(v.daysExpected) }),
    onSuccess: (result) => { showSuccess(`${result.data.created} timesheet(s) created, ${result.data.skipped} already existed.`); queryClient.invalidateQueries({ queryKey: ['timesheets-pending'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const decideTimesheetMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => timesheetApi.decide(id, decision),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['timesheets-pending'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  // ---- Payroll ----
  const periodsQuery = useQuery({ queryKey: ['payroll-periods'], queryFn: () => payrollApi.listPeriods() });
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);
  const periodForm = useForm<{ periodMonth: string }>();
  const createPeriodMutation = useMutation({
    mutationFn: (v: { periodMonth: string }) => payrollApi.createPeriod(v.periodMonth),
    onSuccess: () => { showSuccess('Payroll period created.'); queryClient.invalidateQueries({ queryKey: ['payroll-periods'] }); periodForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const deletePeriodMutation = useMutation({
    mutationFn: (id: number) => payrollApi.deletePeriod(id),
    onSuccess: () => { showSuccess('Payroll period deleted.'); queryClient.invalidateQueries({ queryKey: ['payroll-periods'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete.'),
  });

  const recordsQuery = useQuery({ queryKey: ['payroll-records', selectedPeriodId], queryFn: () => payrollApi.listRecords(selectedPeriodId as number), enabled: !!selectedPeriodId });
  const bulkGenerateRecordsMutation = useMutation({
    mutationFn: () => payrollApi.bulkGenerate(selectedPeriodId as number),
    onSuccess: (result) => {
      showSuccess(`${result.data.generated} record(s) generated.`);
      if (result.data.skipped.length > 0) showError(`${result.data.skipped.length} skipped - see console for details.`);
      queryClient.invalidateQueries({ queryKey: ['payroll-records', selectedPeriodId] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const [deductionDrafts, setDeductionDrafts] = useState<Record<number, { tax: string; pension: string; nhf: string; other: string }>>({});
  const saveDeductionsMutation = useMutation({
    mutationFn: ({ id, d }: { id: number; d: { tax: string; pension: string; nhf: string; other: string } }) =>
      payrollApi.updateDeductions(id, { taxDeduction: Number(d.tax) || 0, pensionDeduction: Number(d.pension) || 0, nhfDeduction: Number(d.nhf) || 0, otherDeductions: Number(d.other) || 0 }),
    onSuccess: () => { showSuccess('Deductions saved.'); queryClient.invalidateQueries({ queryKey: ['payroll-records', selectedPeriodId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const approveRecordMutation = useMutation({
    mutationFn: (id: number) => payrollApi.approve(id),
    onSuccess: () => { showSuccess('Approved.'); queryClient.invalidateQueries({ queryKey: ['payroll-records', selectedPeriodId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const markPaidMutation = useMutation({
    mutationFn: (id: number) => payrollApi.markPaid(id),
    onSuccess: () => { showSuccess('Marked as paid.'); queryClient.invalidateQueries({ queryKey: ['payroll-records', selectedPeriodId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const deleteRecordMutation = useMutation({
    mutationFn: (id: number) => payrollApi.deleteRecord(id),
    onSuccess: () => { showSuccess('Payroll record deleted.'); queryClient.invalidateQueries({ queryKey: ['payroll-records', selectedPeriodId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <h1>Payroll &amp; Timesheets</h1>

      <section className="card">
        <h2>Salary Grades</h2>
        <p className="muted">Sets the default monthly salary for a grade - an individual employee's own salary (set on their profile) always takes priority over this default.</p>
        <form className="inline-form" onSubmit={gradeForm.handleSubmit((v) => createGradeMutation.mutate(v))}>
          <label>Grade Name<input {...gradeForm.register('gradeName', { required: true })} placeholder="e.g. P3" /></label>
          <label>Default Monthly Salary (NGN)<input type="number" {...gradeForm.register('defaultMonthlySalary', { required: true })} /></label>
          <button type="submit" disabled={createGradeMutation.isPending}>Add Grade</button>
        </form>
        {gradesQuery.data && (
          <DataTable
            rows={gradesQuery.data.data}
            rowKey={(g) => g.id}
            emptyMessage="No salary grades configured yet."
            columns={[
              { header: 'Grade', render: (g) => g.grade_name },
              { header: 'Default Monthly Salary', render: (g) => `${g.currency} ${Number(g.default_monthly_salary).toLocaleString()}` },
              { header: '', render: (g) => canDeleteGrades && <button className="link-button" onClick={() => { if (window.confirm(`Delete grade "${g.grade_name}"?`)) deleteGradeMutation.mutate(g.id); }}>Delete</button> },
            ]}
          />
        )}
      </section>

      <section className="card">
        <h2>Timesheets</h2>
        <form className="inline-form" onSubmit={bulkTimesheetForm.handleSubmit((v) => bulkGenerateTimesheetsMutation.mutate(v))}>
          <label>Month<input type="date" {...bulkTimesheetForm.register('periodMonth', { required: true })} /></label>
          <label>Expected Working Days<input type="number" defaultValue={22} {...bulkTimesheetForm.register('daysExpected', { required: true })} /></label>
          <button type="submit" disabled={bulkGenerateTimesheetsMutation.isPending}>Generate for All Active Staff</button>
        </form>

        <h3>Awaiting Your Review</h3>
        {pendingTimesheetsQuery.data && (
          <DataTable
            rows={pendingTimesheetsQuery.data.data}
            rowKey={(t) => t.id}
            emptyMessage="Nothing pending."
            columns={[
              { header: 'Employee', render: (t) => t.employee_name },
              { header: 'Month', render: (t) => t.period_month },
              { header: 'Days Worked', render: (t) => t.days_worked ?? '-' },
              { header: 'Overtime', render: (t) => t.overtime_hours ?? '-' },
              {
                header: '',
                render: (t) => (
                  <span className="row-actions">
                    <button className="link-button" onClick={() => decideTimesheetMutation.mutate({ id: t.id, decision: 'approved' })}>Approve</button>
                    <button className="link-button-danger" onClick={() => decideTimesheetMutation.mutate({ id: t.id, decision: 'rejected' })}>Reject</button>
                    <ReturnAction onReturn={(comment) => timesheetApi.return(t.id, comment)} queryKeysToInvalidate={[['timesheets-pending']]} />
                    <a href={timesheetApi.downloadForHRUrl(t.id, 'pdf')} target="_blank" rel="noreferrer">PDF</a>
                    <a href={timesheetApi.downloadForHRUrl(t.id, 'excel')} target="_blank" rel="noreferrer">Excel</a>
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>

      <section className="card">
        <h2>Payroll Periods</h2>
        <form className="inline-form" onSubmit={periodForm.handleSubmit((v) => createPeriodMutation.mutate(v))}>
          <label>Month<input type="date" {...periodForm.register('periodMonth', { required: true })} /></label>
          <button type="submit" disabled={createPeriodMutation.isPending}>New Period</button>
        </form>
        {periodsQuery.data && (
          <DataTable
            rows={periodsQuery.data.data}
            rowKey={(p) => p.id}
            emptyMessage="No payroll periods yet."
            columns={[
              { header: 'Month', render: (p) => p.period_month },
              { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
              { header: '', render: (p) => <button className="link-button" onClick={() => setSelectedPeriodId(p.id)}>{selectedPeriodId === p.id ? 'Viewing' : 'View Records'}</button> },
              { header: '', render: (p) => canDelete && <button className="link-button" onClick={() => { if (window.confirm(`Delete payroll period "${p.period_month}"? Only allowed if it has no records attached.`)) deletePeriodMutation.mutate(p.id); }}>Delete</button> },
              {
                header: '',
                render: (p) => (
                  <span className="row-actions">
                    <a href={payrollApi.registerUrl(p.id, 'pdf')} target="_blank" rel="noreferrer">Register (PDF)</a>
                    <a href={payrollApi.registerUrl(p.id, 'excel')} target="_blank" rel="noreferrer">Register (Excel)</a>
                    <a href={payrollApi.monthlyReportUrl(p.id, 'pdf')} target="_blank" rel="noreferrer">Monthly Report (PDF)</a>
                    <a href={payrollApi.monthlyReportUrl(p.id, 'excel')} target="_blank" rel="noreferrer">Monthly Report (Excel)</a>
                    <a href={payrollApi.paymentMandateUrl(p.id, 'pdf')} target="_blank" rel="noreferrer">Payment Mandate (PDF)</a>
                    <a href={payrollApi.paymentMandateUrl(p.id, 'excel')} target="_blank" rel="noreferrer">Payment Mandate (Excel)</a>
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>

      {selectedPeriodId && (
        <section className="card">
          <div className="page-header"><h2>Payroll Records</h2><button onClick={() => bulkGenerateRecordsMutation.mutate()} disabled={bulkGenerateRecordsMutation.isPending}>Generate for All Active Staff</button></div>
          {recordsQuery.data && (
            <DataTable
              rows={recordsQuery.data.data}
              rowKey={(r) => r.id}
              emptyMessage="No records generated yet for this period."
              columns={[
                { header: 'Employee', render: (r) => r.employee_name },
                { header: 'Gross', render: (r) => `${r.currency} ${Number(r.gross_salary).toLocaleString()}` },
                { header: 'Net', render: (r) => `${r.currency} ${Number(r.net_salary).toLocaleString()}` },
                { header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                {
                  header: '',
                  render: (r) => (
                    <span className="row-actions">
                      {r.status === 'draft' && (
                        <>
                          <input placeholder="Tax" style={{ width: 70 }} value={deductionDrafts[r.id]?.tax ?? ''} onChange={(e) => setDeductionDrafts((s) => ({ ...s, [r.id]: { ...s[r.id], tax: e.target.value, pension: s[r.id]?.pension ?? '', nhf: s[r.id]?.nhf ?? '', other: s[r.id]?.other ?? '' } }))} />
                          <input placeholder="Pension" style={{ width: 70 }} value={deductionDrafts[r.id]?.pension ?? ''} onChange={(e) => setDeductionDrafts((s) => ({ ...s, [r.id]: { ...s[r.id], pension: e.target.value, tax: s[r.id]?.tax ?? '', nhf: s[r.id]?.nhf ?? '', other: s[r.id]?.other ?? '' } }))} />
                          <input placeholder="NHF" style={{ width: 70 }} value={deductionDrafts[r.id]?.nhf ?? ''} onChange={(e) => setDeductionDrafts((s) => ({ ...s, [r.id]: { ...s[r.id], nhf: e.target.value, tax: s[r.id]?.tax ?? '', pension: s[r.id]?.pension ?? '', other: s[r.id]?.other ?? '' } }))} />
                          <button className="link-button" onClick={() => saveDeductionsMutation.mutate({ id: r.id, d: { tax: deductionDrafts[r.id]?.tax ?? '0', pension: deductionDrafts[r.id]?.pension ?? '0', nhf: deductionDrafts[r.id]?.nhf ?? '0', other: deductionDrafts[r.id]?.other ?? '0' } })}>Save Deductions</button>
                          <button className="link-button" onClick={() => approveRecordMutation.mutate(r.id)}>Approve</button>
                        </>
                      )}
                      {r.status === 'approved' && <button className="link-button" onClick={() => markPaidMutation.mutate(r.id)}>Mark Paid</button>}
                      <a href={payrollApi.payslipForHRUrl(r.id)} target="_blank" rel="noreferrer">Payslip</a>
                      {canDelete && r.status === 'draft' && <button className="link-button" onClick={() => { if (window.confirm('Delete this payroll record?')) deleteRecordMutation.mutate(r.id); }}>Delete</button>}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </section>
      )}
    </div>
  );
}
